/**
 * Справка о том экране, который человек видит сейчас.
 *
 * Кнопка стоит в шапке рядом с настройкой и открывается ещё по F1: искать
 * справку в меню — значит не найти её. Текст короткий по замыслу — три-четыре
 * подсказки о том, что здесь можно сделать; подробности лежат в руководствах,
 * и панель говорит, в каком именно.
 */

import * as React from "react";
import { CircleHelp } from "lucide-react";

import { topicFor } from "@/shared/help/topics";
import { Popover } from "@/shared/ui/Overlays";

export interface HelpButtonProps {
  /** Адрес текущей страницы: по нему выбирается тема. */
  pathname: string;
}

export function HelpButton({ pathname }: HelpButtonProps): React.ReactElement {
  const [open, setOpen] = React.useState(false);
  const topic = topicFor(pathname);

  React.useEffect(() => {
    function onKey(event: KeyboardEvent): void {
      if (event.key === "F1") {
        // Иначе браузер откроет собственную справку поверх страницы
        event.preventDefault();
        setOpen((current) => !current);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <Popover
      align="end"
      open={open}
      onOpenChange={setOpen}
      className="w-[360px] p-[var(--pt-space-16)]"
      trigger={
        <button
          type="button"
          aria-label="Справка по разделу"
          title="Справка по разделу (F1)"
          className="rounded-[var(--pt-radius-3)] p-[var(--pt-space-4)] hover:bg-[var(--pt-bg-hover)] focus-visible:outline-none focus-visible:shadow-[var(--pt-ring)]"
        >
          <CircleHelp className="size-[var(--pt-icon-lg)]" />
        </button>
      }
    >
      <div className="flex flex-col gap-[var(--pt-space-8)]">
        <h2 className="text-[var(--pt-fs-md)] font-medium">{topic.title}</h2>
        <p className="text-[var(--pt-font-ui)] text-[var(--pt-text-secondary)]">{topic.summary}</p>
        <ul className="flex list-disc flex-col gap-[var(--pt-space-4)] pl-[var(--pt-space-16)] text-[var(--pt-font-ui)] text-[var(--pt-text-secondary)]">
          {topic.actions.map((action) => (
            <li key={action}>{action}</li>
          ))}
        </ul>
        <p className="text-[var(--pt-fs-xs)] text-[var(--pt-text-tertiary)]">
          Подробно — «{topic.guide.label}», <code>{topic.guide.file}</code>
        </p>
      </div>
    </Popover>
  );
}
